import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, CardHeader, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import BaseSelectInput from 'views/App/Portal/PerformanceReporting/_components/FieldInputs/BaseSelectInput';

type FlagResolutionValues = {
  disposition: string;
  correctiveActions: string[];
  comments: string;
};

type Props = {
  flagId: string;
  isLoading?: boolean;
  initialValues?: Partial<FlagResolutionValues>;
  onSave: (values: FlagResolutionValues) => void;
};

const FlagResolutionCard: React.FC<Props> = ({ onSave, initialValues, isLoading }) => {
  const [disposition, setDisposition] = useState<string>(initialValues?.disposition ?? '');
  const [correctiveActions, setCorrectiveActions] = useState<string[]>(initialValues?.correctiveActions ?? []);
  const [comments, setComments] = useState<string>(initialValues?.comments ?? '');

  useEffect(() => {
    if (initialValues) {
      setDisposition(initialValues.disposition ?? '');
      setCorrectiveActions(initialValues.correctiveActions ?? []);
      setComments(initialValues.comments ?? '');
    }
  }, [initialValues?.disposition, initialValues?.correctiveActions, initialValues?.comments]);

  const dispositionOptions = useMemo(
    () => [
      {
        value: 'Cleared',
        label: t('performanceReporting.eis.details.flaggedPersonnelDetails.flagResolution.disposition.options.cleared'),
      },
      {
        value: 'Escalated',
        label: t(
          'performanceReporting.eis.details.flaggedPersonnelDetails.flagResolution.disposition.options.escalated',
        ),
      },
      {
        value: 'ActionTaken',
        label: t(
          'performanceReporting.eis.details.flaggedPersonnelDetails.flagResolution.disposition.options.actionTaken',
        ),
      },
      {
        value: 'NoActionRequired',
        label: t(
          'performanceReporting.eis.details.flaggedPersonnelDetails.flagResolution.disposition.options.noActionRequired',
        ),
      },
    ],
    [],
  );

  const correctiveActionOptions = useMemo(
    () => [
      {
        value: 'Training',
        label: t(
          'performanceReporting.eis.details.flaggedPersonnelDetails.flagResolution.correctiveActions.options.training',
        ),
      },
      {
        value: 'Mentoring',
        label: t(
          'performanceReporting.eis.details.flaggedPersonnelDetails.flagResolution.correctiveActions.options.mentoring',
        ),
      },
      {
        value: 'WellnessReferral',
        label: t(
          'performanceReporting.eis.details.flaggedPersonnelDetails.flagResolution.correctiveActions.options.wellnessReferral',
        ),
      },
      {
        value: 'NotifySupervisor',
        label: t(
          'performanceReporting.eis.details.flaggedPersonnelDetails.flagResolution.correctiveActions.options.notifySupervisor',
        ),
      },
    ],
    [],
  );

  if (isLoading) {
    return (
      <Card className='lex-card'>
        <CardHeader title={t('performanceReporting.eis.details.flaggedPersonnelDetails.flagResolution.title')} />
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Expose values to parent via onSave when called
  const handleValuesChange = (updates: Partial<FlagResolutionValues>) => {
    const next = {
      disposition,
      correctiveActions,
      comments,
      ...updates,
    };
    onSave(next);
  };

  return (
    <Card className='lex-card'>
      <CardHeader title={t('performanceReporting.eis.details.flaggedPersonnelDetails.flagResolution.title')} />
      <CardContent>
        <Stack spacing={3}>
          <Box>
            <BaseSelectInput
              options={dispositionOptions}
              value={disposition}
              onChange={(val) => {
                const next = typeof val === 'string' ? val : '';
                setDisposition(next);
                handleValuesChange({ disposition: next });
              }}
              isMulti={false}
              label={t('performanceReporting.eis.details.flaggedPersonnelDetails.flagResolution.disposition.label')}
              placeholder={t(
                'performanceReporting.eis.details.flaggedPersonnelDetails.flagResolution.disposition.placeholder',
              )}
              isClearable
            />
          </Box>
          <Box>
            <BaseSelectInput
              options={correctiveActionOptions}
              value={correctiveActions}
              onChange={(val) => {
                const next = Array.isArray(val) ? val : [];
                setCorrectiveActions(next);
                handleValuesChange({ correctiveActions: next });
              }}
              isMulti={true}
              label={t(
                'performanceReporting.eis.details.flaggedPersonnelDetails.flagResolution.correctiveActions.label',
              )}
              placeholder={t(
                'performanceReporting.eis.details.flaggedPersonnelDetails.flagResolution.correctiveActions.placeholder',
              )}
            />
          </Box>
          <Box>
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{ mb: 1 }}
            >
              {t('performanceReporting.eis.details.flaggedPersonnelDetails.flagResolution.comments.label')}
            </Typography>
            <TextField
              value={comments}
              onChange={(e) => {
                setComments(e.target.value);
                handleValuesChange({ comments: e.target.value });
              }}
              multiline
              rows={4}
              fullWidth
              placeholder={t(
                'performanceReporting.eis.details.flaggedPersonnelDetails.flagResolution.comments.placeholder',
              )}
            />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default FlagResolutionCard;
